// src/render/renderSection.ts

import { renderVideoContent } from "./renderVideoContent";
import { renderImageBackground } from "./renderImageBackground";
import { createNarrationElement, resolveAudioSrc } from "../elements/audio";
import { renderSectionContent } from "./sectionContent";
import { applyOverlay } from "../utils/overlay";
import { initHiddenState } from "../utils/normalize";
import { attachLiteHUD as attachRegularHUD } from "../elements/hudLite";
import { renderMissingMedia } from "./fallback";
import { addSectionArrows } from "../elements/arrow";
import { scanAndRenderInlineImages } from "./imageInline";
import { renderHeaderImages } from "./imageOverlay";
import { Z } from "../utils/constants"; // required: used for arrow z-index
import { enforceBackgroundAudioMix } from "../utils/mediaUtils";
import type { SectionAttrs } from "../types";

export function renderSection(
  section: HTMLElement,
  attrs: SectionAttrs,
  content: string,
  baseUrl: string,
  _counter?: number,
  isVertical?: boolean
) {
  // Derive vertical flag if not provided by caller
  const verticalScreen =
    typeof isVertical === "boolean"
      ? isVertical
      : !matchMedia("(orientation: landscape)").matches;

  // background-color (no image resolution)
  if ((attrs as any)["background-color"]) {
    try {
      (section.style as CSSStyleDeclaration).backgroundColor = String(
        (attrs as any)["background-color"]
      );
    } catch {}
  }

  // --- background ---
  const hasBgImage =
    Boolean((attrs as any).background) ||
    Boolean((attrs as any)["background-h"]) ||
    Boolean((attrs as any)["background-v"]);

  const hasReel =
    typeof (attrs as any)["video-reel"] === "string" ||
    typeof (attrs as any)["video-reel-webm"] === "string";

  const hasNonReelVideo =
    typeof (attrs as any).video === "string" ||
    (attrs as any)["video-h"] ||
    (attrs as any)["video-v"];

  const hasVideo = hasNonReelVideo || hasReel;

  let bg: HTMLElement | null = null;

  if (hasVideo) {
    const isFirstSection =
      section.id === "section-0" ||
      section.getAttribute("data-section-index") === "0";
    bg = renderVideoContent(section, { ...attrs, isVerticalScreen: verticalScreen }, baseUrl, isFirstSection);
  } else if (hasBgImage) {
    bg = renderImageBackground(section, { ...attrs }, baseUrl);
  } else {
    bg = null;
  }

  if (!bg && (hasVideo || hasBgImage)) {
    renderMissingMedia(section, hasVideo ? "video" : "image");
  }

  // --- overlay ---
  if (typeof (attrs as any).overlay === "string") {
    applyOverlay(section, (attrs as any).overlay as string);
  }

  // --- audio narration ---
  const mixesBackgroundVideo = !hasReel && hasNonReelVideo;

  if (attrs.audio && typeof attrs.audio === "string") {
    const src = attrs.audio;

    // Section 0 stays eager; others lazy-load the MP3 just before they’re needed.
    const isFirst =
      section.id === "section-0" ||
      section.getAttribute("data-section-index") === "0";

    // Create the narration element via the new audio stack
    const audioEl = createNarrationElement(src);

    // Ensure it is discoverable by all existing selectors
    audioEl.setAttribute("data-role", "narration");
    audioEl.setAttribute("data-narration", "true");

    // Keep background audio mix synced to narration state changes.
    const onAudioStateChange = () => {
      try { enforceBackgroundAudioMix(section); } catch {}
    };
    audioEl.addEventListener("play", onAudioStateChange);
    audioEl.addEventListener("pause", onAudioStateChange);
    audioEl.addEventListener("ended", onAudioStateChange);

    if (isFirst) {
      // Eager section: keep src and preload as created
      section.appendChild(audioEl);
      section.dataset.hasNarration = "true";
      if (mixesBackgroundVideo) section.dataset.allowAudioMix = "1";
    } else {
      // Lazy-load: stash src and strip the attribute until near viewport
      audioEl.dataset.lazySrc = src;
      audioEl.removeAttribute("src");
      audioEl.preload = "none";

      section.appendChild(audioEl);
      section.dataset.hasNarration = "true";
      if (mixesBackgroundVideo) section.dataset.allowAudioMix = "1";

      // Start fetching slightly before the section becomes visible.
      const root = (document.getElementById("scroll-wrapper") as Element | null) || null;
      const io = new IntersectionObserver((entries, obs) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            if (!audioEl.getAttribute("src") && audioEl.dataset.lazySrc) {
              audioEl.src = resolveAudioSrc(audioEl.dataset.lazySrc);
              try { audioEl.load(); } catch {}
            }
            obs.disconnect();
            break;
          }
        }
      }, { root, rootMargin: "35% 0px", threshold: 0.01 });

      io.observe(section);

      // 🔥 Preload the *next* section's MP3 once THIS section is clearly on screen
      const preheatNext = new IntersectionObserver((entries, obs) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;

          const idx = Number(section.getAttribute("data-section-index") || "0");
          const next = document.querySelector<HTMLElement>(`section[data-section-index="${idx + 1}"]`);

          if (next) {
            const nextAudio = next.querySelector<HTMLAudioElement>(
              'audio[data-section-audio][data-role="narration"]'
            );

            // Only prefetch if it's still lazy (no src yet)
            if (nextAudio && !nextAudio.getAttribute("src") && nextAudio.dataset.lazySrc) {
              // Optional bandwidth guard: only prefetch if autoplay is enabled globally
              const autoplayOn = document.documentElement.getAttribute("data-narration-autoplay") === "1";
              if (autoplayOn) {
                nextAudio.src = resolveAudioSrc(nextAudio.dataset.lazySrc);
                try { nextAudio.load(); } catch {}
                nextAudio.dataset.prefetched = "1";
              }
            }
          }

          obs.disconnect(); // one-shot
          break;
        }
      }, { root, threshold: 0.6 }); // ~60% visible → good lead time

      preheatNext.observe(section);
    }
  } else {
    section.dataset.hasNarration = "false";
    section.removeAttribute("data-allow-audio-mix");
  }

  if (section.dataset.allowAudioMix === "1") {
    const loopShortBackground = (video: HTMLVideoElement) => {
      const explicitOff = (video.dataset.loop || "").toLowerCase() === "false";
      if (explicitOff) return;
      const dur = video.duration || 0;
      if (dur > 0 && dur <= 12) {
        video.loop = true;
        video.setAttribute("loop", "");
        video.dataset.loop = "true";
      }
    };

    section
      .querySelectorAll<HTMLVideoElement>("video[data-role='background'], video[data-bg='true']")
      .forEach((vid) => {
        if (vid.readyState >= HTMLMediaElement.HAVE_METADATA) {
          loopShortBackground(vid);
        } else {
          vid.addEventListener("loadedmetadata", () => loopShortBackground(vid), { once: true });
        }
      });
  }

  // --- content container / layout intent ---
  const layoutH = String(attrs["video-h-layout"] || attrs.align || "").toLowerCase();
  const layoutV = String(attrs["video-v-layout"] || attrs.flow || "").toLowerCase();
  const activeLayout = verticalScreen ? layoutV : layoutH;

  const isOverlay =
    activeLayout === "center" ||
    activeLayout === "middle" ||
    activeLayout === "overlay";

  const isSideSplit =
    ["left", "right"].includes(layoutH) || ["top", "bottom"].includes(layoutV);

  const container = renderSectionContent(attrs, content, { isOverlay, isSideSplit });
  section.appendChild(container);

  // Header/front-matter overlay image(s)
  try {
    renderHeaderImages(section, attrs as any, 0);
  } catch (e) {
    console.warn("[renderSection] overlay image pass failed", e);
  }

  // @@ inline images
  try {
    scanAndRenderInlineImages(section, 0);
  } catch (e) {
    console.warn("[renderSection] inline image pass failed", e);
  }

  // Keep overlay wrappers visible; only the <img> animates.
  section
    .querySelectorAll<HTMLElement>('[data-role="image-overlay-container"] .section-image')
    .forEach((host) => {
      if ((host.dataset.animated || "").toLowerCase() !== "true") host.dataset.animated = "true";
      host.style.visibility = "visible"; // why: defensive against priming
      host.style.opacity = "1";
    });

  // --- init hidden state for reveals ---
  initHiddenState(section);
  section.dataset.revealed = "0";

  // --- announce readiness (GSAP/observers hook) ---
  try {
    section.dispatchEvent(
      new CustomEvent("sc:section-ready", {
        bubbles: true,
        detail: { section, attrs, isVertical: verticalScreen, hasVideo, isOverlay, isSideSplit },
      })
    );
  } catch {
    /* no-op */
  }

  // --- scroll arrows (only if specified) ---
  try {
    if (attrs.arrow) {
      const arrowAttr = Array.isArray(attrs.arrow) ? attrs.arrow : String(attrs.arrow);
      addSectionArrows(section, arrowAttr, Z.HUD); // why: keeps arrows above content/HUD layering contract
    }
  } catch (e) {
    console.warn("[renderSection] addSectionArrows failed", e);
  }

  // --- HUD (after content & readiness) ---
  if (attrs.hud !== "off") {
    requestAnimationFrame(() => {
      try {
        attachRegularHUD(section);
      } catch (e) {
        console.warn("[renderSection] attachRegularHUD failed", e);
      }
    });
  }

  return section;
}
